import { api } from './client'

export interface Organization {
  id: string
  name: string
  slug: string
  created_at: string
  archived_at: string | null
}

export interface Workspace {
  id: string
  organization_id: string
  slug: string
  name: string
  expires_at: string | null
  archived_at: string | null
}

export interface Member {
  user_id: string
  email: string
  name: string | null
  role: string
  granted_at: string
  revoked_at: string | null
  module_slugs: string[]
}

export interface ModuleCatalogEntry {
  slug: string
  name: string
  description: string | null
  is_active: boolean
}

export interface WorkspaceModule {
  slug: string
  name: string
  enabled_at: string
}

export interface AddMemberResult {
  member: Member
  invite_url: string | null
}

export interface AuditEntry {
  id: number
  workspace_id: string | null
  user_id: string | null
  user_email: string | null
  action: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface AuditQuery {
  workspace_id?: string
  organization_id?: string
  user_id?: string
  email?: string
  action?: string
  from?: string
  to?: string
  limit?: number
  offset?: number
}

export interface AuditAggregateBucket {
  label: string
  count: number
}

export interface AuditAggregate {
  by_day: AuditAggregateBucket[]
  by_action: AuditAggregateBucket[]
  by_user: AuditAggregateBucket[]
  total: number
}

export interface AuditAggregateQuery {
  workspace_id?: string
  organization_id?: string
  user_id?: string
  days?: number
}

export const adminApi = {
  // Organizations
  listOrganizations: () =>
    api.get<Organization[]>('/api/admin/organizations'),
  createOrganization: (name: string, slug: string) =>
    api.post<Organization>('/api/admin/organizations', { name, slug }),

  // Workspaces
  listWorkspaces: (organizationId?: string) =>
    api.get<Workspace[]>(
      organizationId
        ? `/api/admin/workspaces?organization_id=${organizationId}`
        : '/api/admin/workspaces',
    ),
  createWorkspace: (
    organization_id: string,
    slug: string,
    name: string,
    expires_at: string | null,
  ) =>
    api.post<Workspace>('/api/admin/workspaces', {
      organization_id,
      slug,
      name,
      expires_at,
    }),
  archiveWorkspace: (id: string) =>
    api.post<Workspace>(`/api/admin/workspaces/${id}/archive`),

  // Members
  listMembers: (workspaceId: string) =>
    api.get<Member[]>(`/api/admin/workspaces/${workspaceId}/members`),
  addMember: (
    workspaceId: string,
    email: string,
    name: string | null,
    sendInvite: boolean,
  ) =>
    api.post<AddMemberResult>(
      `/api/admin/workspaces/${workspaceId}/members`,
      { email, name, send_invite: sendInvite },
    ),
  revokeMember: (workspaceId: string, userId: string) =>
    api.delete<void>(
      `/api/admin/workspaces/${workspaceId}/members/${userId}`,
    ),
  setUserPassword: (userId: string, newPassword: string) =>
    api.post<void>(`/api/admin/users/${userId}/set-password`, {
      new_password: newPassword,
    }),
  setMemberModules: (
    workspaceId: string,
    userId: string,
    moduleSlugs: string[],
  ) =>
    api.put<Member>(
      `/api/admin/workspaces/${workspaceId}/members/${userId}/modules`,
      { module_slugs: moduleSlugs },
    ),

  // Audit log
  listAudit: (q: AuditQuery = {}) => {
    const sp = new URLSearchParams()
    if (q.workspace_id) sp.set('workspace_id', q.workspace_id)
    if (q.organization_id) sp.set('organization_id', q.organization_id)
    if (q.user_id) sp.set('user_id', q.user_id)
    if (q.email) sp.set('email', q.email)
    if (q.action) sp.set('action', q.action)
    if (q.from) sp.set('from', q.from)
    if (q.to) sp.set('to', q.to)
    if (q.limit !== undefined) sp.set('limit', String(q.limit))
    if (q.offset !== undefined) sp.set('offset', String(q.offset))
    const qs = sp.toString()
    return api.get<AuditEntry[]>(
      `/api/admin/audit${qs ? '?' + qs : ''}`,
    )
  },
  aggregateAudit: (q: AuditAggregateQuery = {}) => {
    const sp = new URLSearchParams()
    if (q.workspace_id) sp.set('workspace_id', q.workspace_id)
    if (q.organization_id) sp.set('organization_id', q.organization_id)
    if (q.user_id) sp.set('user_id', q.user_id)
    if (q.days !== undefined) sp.set('days', String(q.days))
    const qs = sp.toString()
    return api.get<AuditAggregate>(
      `/api/admin/audit/aggregate${qs ? '?' + qs : ''}`,
    )
  },

  // Modules
  listModuleCatalog: () =>
    api.get<ModuleCatalogEntry[]>('/api/admin/modules'),
  listWorkspaceModules: (workspaceId: string) =>
    api.get<WorkspaceModule[]>(
      `/api/admin/workspaces/${workspaceId}/modules`,
    ),
  enableModule: (workspaceId: string, moduleSlug: string) =>
    api.post<WorkspaceModule>(
      `/api/admin/workspaces/${workspaceId}/modules`,
      { module_slug: moduleSlug },
    ),
  disableModule: (workspaceId: string, moduleSlug: string) =>
    api.delete<void>(
      `/api/admin/workspaces/${workspaceId}/modules/${moduleSlug}`,
    ),
}
